import { type Configuration, PublicClientApplication } from '@azure/msal-browser';

/**
 * Modo dev del frontend — ⚠️ NUNCA en producción.
 *
 * Doble blindaje:
 *   1. `import.meta.env.DEV` solo es true en `vite dev`. Builds de producción
 *      (`vite build`) reemplazan esto por `false` en tiempo de compilación, así
 *      que el código de modo dev queda eliminado por tree-shaking.
 *   2. `VITE_AUTH_DEV_MODE === 'true'` requiere opt-in explícito.
 *
 * En modo dev:
 *   - No se instancia MSAL.
 *   - El cliente HTTP omite el header Authorization.
 *   - El backend (api/) detecta `AUTH_DEV_MODE=true` en local.settings.json y
 *     usa el mismo usuario dummy (ver `api/src/auth/middleware.ts`).
 */
export const modoDev = import.meta.env.DEV && import.meta.env.VITE_AUTH_DEV_MODE === 'true';

export const usuarioDev = {
  name: 'Dev User',
  username: 'dev@adelante.cr',
  iniciales: 'DU',
} as const;

export const configuracionMsal: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
};

/** Scope custom expuesto por la API Adelante (App Registration de la API). */
export const scopesApi = [import.meta.env.VITE_AZURE_API_SCOPE];

/** Scope mínimo de Microsoft Graph para obtener el perfil del usuario. */
export const scopesLogin = ['User.Read'];

/** Deriva las iniciales (máximo 2) a partir de un nombre completo. */
export function obtenerIniciales(nombre: string): string {
  return nombre
    .split(' ')
    .map((parte) => parte[0] ?? '')
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/**
 * Instancia singleton de MSAL. `null` en modo dev (para evitar inicializar
 * MSAL sin App Registration configurada).
 */
export const msalInstance = modoDev ? null : new PublicClientApplication(configuracionMsal);

/**
 * Cierra la sesión Entra y redirige al login. En modo dev no hace nada (no hay
 * sesión MSAL real).
 */
export async function cerrarSesion(): Promise<void> {
  if (modoDev || !msalInstance) return;
  const cuenta = msalInstance.getAllAccounts()[0];
  await msalInstance.logoutRedirect({
    account: cuenta,
    postLogoutRedirectUri: window.location.origin,
  });
}

/**
 * "Recargar token": cuando la app se cuelga por token caducado, limpia la caché
 * de MSAL y fuerza una re-adquisición interactiva. Si el silencioso falla,
 * cae a redirect (re-login). Recarga la página al volver para reintentar las
 * queries con el token fresco.
 */
export async function recargarToken(): Promise<void> {
  if (modoDev || !msalInstance) {
    window.location.reload();
    return;
  }
  const cuenta = msalInstance.getAllAccounts()[0];
  if (!cuenta) {
    // Sin cuenta → re-login completo.
    await msalInstance.loginRedirect({ scopes: scopesLogin });
    return;
  }
  try {
    // Forzar refresh del token de la API (forceRefresh ignora la caché).
    await msalInstance.acquireTokenSilent({
      scopes: scopesApi,
      account: cuenta,
      forceRefresh: true,
    });
    // Token renovado en caché → recargar para reintentar las llamadas.
    window.location.reload();
  } catch {
    // El silencioso falló (sesión realmente expirada) → redirect interactivo.
    await msalInstance.acquireTokenRedirect({ scopes: scopesApi, account: cuenta });
  }
}
